import React from 'react';
import graphql from "babel-plugin-relay/macro";
import {usePreloadedQuery} from "react-relay";
import {useLoaderData} from "react-router-dom";
import {useUser} from "../../hooks/useUser";
import DetailsPageHeader from "../../components/common/headers/DetailsPageHeader";
import FacilityLocationVersions from "./versions/FacilityLocationVersions";
import {Col, Row} from "react-bootstrap";
import FacilityLocationTaxonomies from "./taxonomies/FacilityLocationTaxonomies";
import FacilityLocationPracticePeriods from "./practice-periods/FacilityLocationPracticePeriods";
import FacilityLocationLineOfService from "./linesOfService/FacilityLocationLineOfService";
import DeletePanel from "../../components/common/repeated-page-panels/DeletePanel";
import FacilityLocationLanguages from "./languages/FacilityLocationLanguages";
import FacilityLocationCmsSpecialties from "./cmsSpecialties/FacilityLocationCmsSpecialties";

const FacilityLocationDetails = () => {
    const loader = useLoaderData();
    const data = usePreloadedQuery(graphql`
        query FacilityLocationDetailsQuery($id: ID!) {
            node(id:$id) {
                id
                ... on FacilityLocation {
                    facility {
                        id
                        applicableVersion {
                            displayName
                        }
                        legalContract {
                            linesOfService {
                                ...ContractLinesOfServiceFragments_LinesOfService
                            }
                        }
                        identifiers {
                            identifierType {
                                code
                            }
                            value
                        }
                    }
                    location {
                        addressLine1
                        addressLine2
                        city
                        state
                        postalCode
                        applicableVersion {
                            name
                        }
                    }
                    legalContractLineOfService {
                        id
                        lineOfService {
                            id
                            name
                        }
                    }
                    languages {
                        ...FacilityLocationLanguages_Languages
                    }
                    linesOfService {
                        ...FacilityLocationLineOfService
                    }
                    cmsSpecialties {
                        ...FacilityLocationCmsSpecialties_CmsSpecialties
                    }
                    versions {
                        ...FacilityLocationVersions_Versions
                    }
                    taxonomies {
                        ...FacilityLocationTaxonomies_Taxonomy
                    }
                    practicePeriods {
                        ...FacilityLocationPracticePeriods_PracticePeriod
                    }
                }
            }
            allLanguages {
                ...Fragments_AllLanguagesDropdown
            }
            allCmsSpecialties {
                ...Fragments_AllCmsSpecialtiesDropdown
            }
            allTaxonomies {
                ...Fragments_AllTaxonomiesDropdown
            }
        }
    `, loader.facilityLocationDetailsQuery);
    const deleteMutation = graphql`
    mutation FacilityLocationDetails_DeleteMutation($id:ID!) {
        deleteNode(input:{id: $id}) {
                deletedNodeId @deleteRecord
            owner {
                ... on Facility {
                    locations {
                        ...FacilityLocations_Locations
                    }
                }
                ... on Location {
                    facilities {
                        ...LocationFacilities_Facilities
                    }
                }
            }
        }
    }`
    const {hasPermission} = useUser();
    const providerNpi = data.node.facility.identifiers.filter(x => x.identifierType?.code === "NPI").map(y => y.value).sort().join(", ");
    const titleElement = (
        <>
            {data.node.facility.applicableVersion.displayName}{providerNpi ? <small> ({providerNpi})</small> : ""}<br/>
            at {data.node.location.applicableVersion.name} <small>({data.node.location.addressLine1} {data.node.location.addressLine2} {data.node.location.city}, {data.node.location.state} {data.node.location.postalCode})</small>, {data.node.legalContractLineOfService.lineOfService.name}
        </>
    )
    return (
        <>
            <DetailsPageHeader iconText={"Facility Location"} 
                               iconType={"building"}
                               title={titleElement} 
            />
            <Row>
                <Col>
                    <FacilityLocationVersions facilityLocationId={data.node.id}
                                                  versions={data.node.versions}
                    />
                </Col>
            </Row>
            <Row>
                <Col>
                    <FacilityLocationTaxonomies facilityLocationId={data.node.id}
                                                    taxonomies={data.node.taxonomies}
                                                    allTaxonomies={data.allTaxonomies}
                    />
                </Col>
                <Col>
                    <FacilityLocationCmsSpecialties facilityLocationId={data.node.id}
                                                    cmsSpecialties={data.node.cmsSpecialties}
                                                    allCmsSpecialties={data.allCmsSpecialties}
                    />
                </Col>
            </Row>
            <Row>
                <Col>
                    <FacilityLocationLanguages facilityLocationId={data.node.id}
                                               allLanguages={data.allLanguages}
                                               languages={data.node.languages}
                    />
                </Col>
                <Col>
                    <FacilityLocationPracticePeriods facilityLocationId={data.node.id}
                                                     practicePeriods={data.node.practicePeriods}
                    />
                </Col>
            </Row>
            <Row>
                {hasPermission("FacilityLocation.Delete") && <DeletePanel mutation={deleteMutation} 
                                                                           redirectUrl={"/facilities/" + data.node.facility.id} 
                                                                           thingToDelete={"Facility Location"} 
                                                                           id={data.node.id}>
                    To delete a Facility Location, you must have only 1 version, only 1 line of service and no other related data.
                </DeletePanel>}
            </Row>
            
        </>
    )
}
export default FacilityLocationDetails